import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { staggerContainer, fadeInUp, scaleIn } from '../hooks/useScrollAnimation'

const skillCategories = [
  {
    id: 'backend',
    label: 'Backend',
    icon: '◉',
    color: '#00ff41',
    skills: [
      { name: 'Node.js / Express', level: 95 },
      { name: 'TypeScript', level: 90 },
      { name: 'REST API Design', level: 95 },
      { name: 'Microservices', level: 88 },
      { name: 'GraphQL', level: 82 },
      { name: 'LangChain / LLM Pipelines', level: 85 },
    ],
  },
  {
    id: 'database',
    label: 'Database',
    icon: '◎',
    color: '#00d4ff',
    skills: [
      { name: 'PostgreSQL', level: 90 },
      { name: 'MongoDB', level: 88 },
      { name: 'Redis', level: 88 },
      { name: 'MySQL', level: 80 },
      { name: 'DB Indexing & Query Opt.', level: 90 },
    ],
  },
  {
    id: 'frontend',
    label: 'Frontend',
    icon: '◈',
    color: '#a78bfa',
    skills: [
      { name: 'React.js', level: 85 },
      { name: 'Next.js', level: 80 },
      { name: 'HTML5 / CSS3', level: 85 },
      { name: 'Material-UI', level: 82 },
      { name: 'FusionChart', level: 75 },
    ],
  },
  {
    id: 'devops',
    label: 'DevOps & Cloud',
    icon: '◆',
    color: '#ffa657',
    skills: [
      { name: 'Docker', level: 88 },
      { name: 'Kubernetes', level: 78 },
      { name: 'AWS / GCP', level: 82 },
      { name: 'Nginx', level: 80 },
      { name: 'Shell Scripting (Bash)', level: 78 },
      { name: 'Jest (Testing)', level: 85 },
    ],
  },
]

const techBadges = [
  'Node.js', 'TypeScript', 'JavaScript', 'LangChain', 'GPT-4o',
  'PostgreSQL', 'MongoDB', 'Redis', 'MySQL', 'React.js', 'Next.js',
  'Docker', 'Kubernetes', 'AWS', 'GCP', 'GraphQL', 'REST APIs',
  'Microservices', 'Nginx', 'Jest', 'Java', 'Python', 'Bash',
]

function SkillBar({ name, level, color, isInView, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      className="group"
    >
      <div className="flex justify-between items-center mb-1.5">
        <span className="font-mono text-sm text-[#e6edf3] group-hover:text-[#00ff41] transition-colors">
          {name}
        </span>
        <span className="font-mono text-xs text-[#6e7681]">{level}%</span>
      </div>
      <div className="h-1.5 bg-[#30363d] rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={isInView ? { width: `${level}%` } : { width: 0 }}
          transition={{ delay: index * 0.08 + 0.3, duration: 1.2, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${color}, ${color}88)`,
            boxShadow: `0 0 8px ${color}66`,
          }}
        />
      </div>
    </motion.div>
  )
}

export default function Skills() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -80px 0px' })
  const [activeCategory, setActiveCategory] = useState('frontend')

  const active = skillCategories.find((c) => c.id === activeCategory)

  return (
    <section id="skills" className="py-24 relative bg-[#0d1117]">
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" ref={ref}>
        {/* Header */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-16"
        >
          <p className="font-mono text-[#00ff41] text-sm mb-2">// 02. skills</p>
          <h2 className="section-title">
            Tech <span>Stack</span>
          </h2>
          <div className="h-0.5 w-24 bg-gradient-to-r from-[#00ff41] to-transparent mt-3" />
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="flex flex-wrap gap-3 mb-10"
        >
          {skillCategories.map((cat) => (
            <motion.button
              key={cat.id}
              variants={scaleIn}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-mono text-sm border transition-all duration-300 ${
                activeCategory === cat.id
                  ? 'bg-[#00ff41]/10 border-[#00ff41] text-[#00ff41]'
                  : 'border-[#30363d] text-[#6e7681] hover:border-[#00ff41]/40 hover:text-[#e6edf3]'
              }`}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <span style={{ color: cat.color }}>{cat.icon}</span>
              {cat.label}
            </motion.button>
          ))}
        </motion.div>

        {/* Skills Panel */}
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Skill Bars */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="glass-card p-6 border border-[#30363d] rounded-xl"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="text-xl"
                    style={{ color: active.color }}
                  >
                    {active.icon}
                  </span>
                  <h3 className="font-mono text-lg font-semibold text-[#e6edf3]">
                    {active.label}
                  </h3>
                  <div
                    className="h-px flex-1"
                    style={{
                      background: `linear-gradient(90deg, ${active.color}40, transparent)`,
                    }}
                  />
                </div>
                <div className="space-y-5">
                  {active.skills.map((skill, i) => (
                    <SkillBar
                      key={skill.name}
                      name={skill.name}
                      level={skill.level}
                      color={active.color}
                      isInView={isInView}
                      index={i}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* All Tech Badges */}
          <div className="lg:col-span-2">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              className="glass-card p-6 border border-[#30363d] rounded-xl h-full"
            >
              <div className="flex items-center gap-2 mb-5">
                <span className="text-[#00ff41] font-mono text-xs">$</span>
                <span className="font-mono text-sm text-[#e6edf3]">
                  list --all-technologies
                </span>
              </div>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="flex flex-wrap gap-2"
              >
                {techBadges.map((tech, i) => (
                  <motion.span
                    key={tech}
                    variants={{
                      hidden: { opacity: 0, scale: 0.8 },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: { delay: i * 0.04, duration: 0.3 },
                      },
                    }}
                    className="tag cursor-default"
                    whileHover={{
                      scale: 1.1,
                      borderColor: '#00ff41',
                      backgroundColor: 'rgba(0,255,65,0.15)',
                    }}
                  >
                    {tech}
                  </motion.span>
                ))}
              </motion.div>

              {/* Proficiency legend */}
              <div className="mt-6 pt-4 border-t border-[#30363d]">
                <p className="font-mono text-xs text-[#6e7681] mb-3">// proficiency scale</p>
                <div className="space-y-1.5">
                  {[
                    { range: '90-100%', label: 'Expert', color: '#00ff41' },
                    { range: '75-89%', label: 'Advanced', color: '#00d4ff' },
                    { range: '60-74%', label: 'Proficient', color: '#a78bfa' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-mono text-xs text-[#6e7681]">
                        {item.range} — {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
